import type { FieldPolicy } from '@apollo/client';
import type { FieldNode } from 'graphql';
import isEmpty from 'lodash.isempty';

const ROOT_QUERY_FIELDS_WHITELIST = Object.freeze([
  'carriageGroups',
  'classifiers',
  'cms',
  'configuration',
  'filestorage',
  'indicators',
  'journal',
  'notification',
  'rbac',
  'scheduler',
  'sync',
  'tpc',
  'train',
  'train_schedules',
  'train_schemas',
  'users',
]);

type Context = {
  typename: string;
  fieldName: string;
  field: FieldNode | null;
  variables?: Record<string, unknown>;
};

type FieldArgument = {
  name: { kind: string; value: string };
  value: { value?: string | boolean; fields?: FieldArgument[] };
};

function parseArgument(
  argument: FieldArgument,
  memo: Record<string, unknown> = {},
  path: string = '',
): Record<string, unknown> {
  const value = argument.value;
  const name = path ? `${path}.${argument.name.value}` : argument.name.value;

  if (value.value) {
    Reflect.set(memo, name, value.value);
  } else if (value.fields) {
    value.fields.forEach(field => parseArgument(field, memo, name));
  }

  return memo;
}

function findComplexSelection(field: FieldNode): FieldNode | undefined {
  const selections = field.selectionSet?.selections as FieldNode[] | undefined;
  return selections?.find(selection => Boolean(selection.selectionSet));
}

function concatFieldNodeIdentifiers(
  field: FieldNode,
  identifier: string,
): string {
  const fieldName = field.name.value;
  const name = identifier ? `${identifier}:${fieldName}` : fieldName;

  const fieldArguments = field.arguments;
  const parsedFieldArguments = fieldArguments?.reduce((dictionary, arg) => {
    const currentArgument = parseArgument(arg as FieldArgument);
    return { ...dictionary, ...currentArgument };
  }, {});
  const currentIdentifier = isEmpty(parsedFieldArguments)
    ? name
    : `${name}:${JSON.stringify(parsedFieldArguments)}`;

  const nestedField = findComplexSelection(field);
  if (fieldArguments?.length || !nestedField) return currentIdentifier;

  return concatFieldNodeIdentifiers(nestedField, currentIdentifier);
}

function generateRootFieldIdentifier(context: Context): string {
  const { variables, field } = context;

  const variablesString = !isEmpty(variables) ? JSON.stringify(variables) : '';

  const nestedField = field ? findComplexSelection(field) : null;
  if (!nestedField) return variablesString;

  const identifier = concatFieldNodeIdentifiers(nestedField, '');
  return variablesString ? `${identifier}:${variablesString}` : identifier;
}

/** Конфиг для ручной генерации кэш ключей вешних оберток нашей gql схемы.
 *
 * - Зачем ? - Apollo из коробки не умеет кешировать вложенные операции, а
 * у нас все операции вложенные.
 * - Каким образом генерируем ? - Бежим по gql ноду сверху от внешней обертки вниз до первой
 * операции и конкатенируем названия промежуточных филдов и их аргументы.
 * - Какие ограничения использования ?
 *    1. Необходимо постоянно акутализировать массив названий внешних оберток. (от этого не уйти)
 *    2. В рамках одного gql нода на одном уровне вложенности может быть определена только одна операция.
 *  Операция - это филд с аргументами. (от этого не уйти)
 *    3. Отсутствует поддержка кейсов когда одна операция вложена в другую. Это можно решить изменив
 *  условие выхода из рекурсии и конкатенировать идентификаторы не до первой встреченной операции,
 *  а идти глубже.
 */
export const ROOT_FIELD_POLICY_CONFIG = Object.freeze(
  ROOT_QUERY_FIELDS_WHITELIST.reduce<Record<string, FieldPolicy<unknown>>>(
    (obj, key) => {
      obj[key] = {
        keyArgs(_: unknown, context: Context) {
          return generateRootFieldIdentifier(context);
        },
      };
      return obj;
    },
    {},
  ),
);
